import { zodResolver } from '@hookform/resolvers/zod'
import { SmartCaptcha } from '@yandex/smart-captcha'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'
import { z } from 'zod'


import SmsCodeConfirm from '@features/auth/ui/form/sms-code-confirm'
import styles from '@features/auth/ui/form/styles.module.scss'
import { useErrorInfoModal } from '@features/modal'
import useModalStore from '@features/modal/model'


import { handlePhonePaste } from '@shared/__HACKS'
import { URL_PAGE_POLICY } from '@shared/contants'
import { useLoginOrRegisterMutation } from '@shared/generated'
import Button from '@shared/ui/button'
import ErrorMessage from '@shared/ui/error-message'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@shared/ui/form'
import MaskedInput from '@shared/ui/input-mask'
import { PHONE_MASK_LENGTH } from '@shared/validation'




export default function LoginOrRegister () {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { modals, openModal, closeModal } = useModalStore()
  const { t: tv } = useTranslation('validation')
  const { t: ta } = useTranslation('auth')
  const { t: tc } = useTranslation('common')
  const isTablet = useMediaQuery('(max-width: 991px)')
  const [ sendError, setSendError ] = useState<boolean>(false)
  const [ captchaToken, setCaptchaToken ] = useState<string | null>(null)
  const [ captchaError, setCaptchaError ] = useState<boolean>(false)
  const [ LoginOrRegister ] = useLoginOrRegisterMutation()

  const formSchema = z.object({
    phone: z.string().min(PHONE_MASK_LENGTH, {
      message: tv('phone'),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const loginModalCloseCallback =
      modals.get('loginOrRegister')?.config?.onClose
    const { phone } = values

    if (!captchaToken) {
      setCaptchaError(true)
      return
    }

    setCaptchaError(false)

    try {
      const { data } = await LoginOrRegister({
        variables: {
          phone: phone,
          token: captchaToken,
        },
      })

      if (data?.loginOrRegister) {
        const loginOrRegister = data.loginOrRegister

        if (loginOrRegister.error) {
          setSendError(true)

          return
        }

        if (loginOrRegister.sent) {
          closeModal('loginOrRegister', true)
          openModal(
            'smsCodeConfirm',
            <SmsCodeConfirm
              id={loginOrRegister.id as string}
              numberPhone={phone}
              timeout={loginOrRegister.timeout as number}
              onCloseCallback={loginModalCloseCallback}
            />,
            {
              title: ta('sms.code.title'),
              onClose: () => {
                if (loginModalCloseCallback) {
                  loginModalCloseCallback()
                }
              },
            },
          )
        }

        setSendError(false)
      }
    }
    catch (error) {
      console.log(error)
      openErrorInfoModal()
      setSendError(true)
    }
  }

  return (
    <div className={styles.root}>
      <Form {...form}>
        <form
          className={styles.root__form}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <MaskedInput
                    {...field}
                    placeholder="+7 000 000 00 00"
                    inputMode="numeric"
                    autoFocus
                    onPaste={handlePhonePaste}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className={styles['root__group-button']}>
            <Button type="submit" size={isTablet ? 'medium' : 'large'}>
              {ta('get.auth.code')}
            </Button>
          </div>
        </form>
      </Form>
      <SmartCaptcha
        sitekey={import.meta.env.VITE_YANDEX_CAPTCHA_TOKEN}
        onSuccess={(token: string) => {
          setCaptchaToken(token)
        }}
      />
      {sendError && <ErrorMessage>{tc('error_submit')}</ErrorMessage>}
      {captchaError && <ErrorMessage>{tc('captcha')}</ErrorMessage>}{' '}
      <p className={styles.root__bottom}>
        <Trans
          i18nKey={ta('code.privacy.note')}
          components={[
            <></>,
            <Link
              to={URL_PAGE_POLICY}
              target="_blank"
              rel="noopener noreferrer"
            />,
          ]}
        />
      </p>
    </div>
  )
}
